import { useEffect, useState } from "react";
import { Share2, Check, Link2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * KaraokeSharePanel — reusable "Share to socials" panel for any
 * karaoke_jobs row (including lyric-video / vocal_only=true renders
 * from the AI Vocalist Studio).
 *
 * Fetches `/api/production/karaoke/{jobId}/share-status` on mount,
 * renders per-platform toggle cards, a caption textarea, and a single
 * "Post to N platforms" button that hits the existing dispatcher via
 * `POST /api/production/karaoke/{jobId}/share`.
 *
 * Props:
 *   jobId          string  — karaoke job id (kk_* or the vocal-only variant)
 *   defaultCaption string  — initial caption text
 *   testidPrefix   string  — for data-testid uniqueness on the parent surface
 */
const PLATFORM_LABELS = {
  tiktok:    { name: "TikTok",    accent: "#25F4EE" },
  instagram: { name: "Reels",     accent: "#E1306C" },
  youtube:   { name: "Shorts",    accent: "#FF0000" },
  facebook:  { name: "Facebook",  accent: "#1877F2" },
  x:         { name: "X",         accent: "#a9b3c9" },
  threads:   { name: "Threads",   accent: "#a78bfa" },
};

export function KaraokeSharePanel({ jobId, defaultCaption = "", testidPrefix = "karaoke-share" }) {
  const [shareStatus, setShareStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [caption, setCaption] = useState(defaultCaption);
  const [sharing, setSharing] = useState(false);

  // ── Fetch share status on mount / jobId change ───────────────
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/production/karaoke/${jobId}/share-status`);
        if (cancelled) return;
        const s = r.data || null;
        setShareStatus(s);
        setSelected(new Set(s?.connected || []));
      } catch (e) {
        if (!cancelled) setShareStatus(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [jobId]);

  const toggle = (pl) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pl)) next.delete(pl); else next.add(pl);
      return next;
    });
  };

  const post = async () => {
    if (!jobId) return;
    const platforms = Array.from(selected);
    if (platforms.length === 0) {
      toast.error("Pick at least one platform first");
      return;
    }
    setSharing(true);
    try {
      const r = await api.post(`/production/karaoke/${jobId}/share`, {
        platforms,
        caption,
      });
      const d = r.data || {};
      // Refresh
      try {
        const s2 = await api.get(`/production/karaoke/${jobId}/share-status`);
        setShareStatus(s2.data || null);
      } catch { /* noop */ }

      const queued = d.queued || [];
      const skipped = d.skipped || [];
      if (queued.length > 0) {
        toast.success(`Queued ${queued.length} post${queued.length === 1 ? "" : "s"} — publisher runs on the next tick`);
      }
      if (skipped.length > 0) {
        const dup = skipped.filter((s) => s.reason === "recent_duplicate").length;
        const nc  = skipped.filter((s) => s.reason === "not_connected").length;
        const bits = [];
        if (dup > 0) bits.push(`${dup} already queued in the last 10 min`);
        if (nc  > 0) bits.push(`${nc} not connected`);
        if (bits.length > 0) toast.message(`Skipped: ${bits.join(" · ")}`);
      }
      if (queued.length === 0 && skipped.length === 0) {
        toast.message("Nothing to queue — pick a connected platform first");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Share failed"));
    } finally {
      setSharing(false);
    }
  };

  if (loading) {
    return (
      <div className="mt-3 flex items-center gap-2 type-caption text-neutral-500"
           data-testid={`${testidPrefix}-loading`}>
        <Loader2 size={12} className="animate-spin" />
        Loading share panel…
      </div>
    );
  }

  if (!shareStatus || !(shareStatus.supported || []).length) {
    return null;
  }

  return (
    <div className="mt-3 pt-3 border-t border-[#2a2f3a]" data-testid={`${testidPrefix}-panel`}>
      <div className="flex items-center gap-2 mb-2">
        <Share2 size={13} style={{ color: "#4a9eff" }} />
        <div className="text-white" style={{ fontSize: 13, fontWeight: 600 }}>
          Share to socials
        </div>
      </div>

      {/* Platform pills */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        {(shareStatus.supported || []).map((pl) => {
          const meta = PLATFORM_LABELS[pl] || { name: pl, accent: "#a9b3c9" };
          const connected = (shareStatus.connected || []).includes(pl);
          const active = selected.has(pl);
          const latest = shareStatus.latest_posts?.[pl];
          return (
            <button
              key={pl}
              type="button"
              disabled={!connected || sharing}
              onClick={() => toggle(pl)}
              data-testid={`${testidPrefix}-toggle-${pl}`}
              className="text-left px-3 py-2 rounded-md border transition disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                background: "#171a24",
                borderColor: active ? meta.accent : "#2a2f3a",
                boxShadow: active ? `0 0 0 1px ${meta.accent} inset` : "none",
              }}
            >
              <div className="flex items-center justify-between">
                <div className="text-white" style={{ fontSize: 13, fontWeight: 500 }}>{meta.name}</div>
                {active && <Check size={11} style={{ color: meta.accent }} />}
              </div>
              <div className="type-caption mt-0.5"
                   style={{ color: connected ? "#3ecf8e" : "#7a8399" }}>
                {connected ? (latest?.status || "Ready") : "Not connected"}
              </div>
              {latest?.external_url && (
                <a
                  href={latest.external_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 mt-1 type-caption hover:underline"
                  style={{ color: "#4a9eff" }}
                >
                  <Link2 size={9} /> View post
                </a>
              )}
            </button>
          );
        })}
      </div>

      {/* Caption */}
      <textarea
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        rows={2}
        maxLength={2200}
        disabled={sharing}
        placeholder="Write a caption for your lyric video…"
        data-testid={`${testidPrefix}-caption`}
        className="w-full px-3 py-2 rounded-md text-white resize-none disabled:opacity-50"
        style={{ background: "#171a24", border: "1px solid #2a2f3a", fontSize: 13 }}
      />
      <div className="flex items-center justify-between mt-2">
        <div className="type-caption text-neutral-500">
          {caption.length}/2200
          {(shareStatus.not_connected || []).length > 0 && (
            <>
              {" · "}
              <a href="/dashboard/socials" target="_blank" rel="noopener noreferrer"
                 className="hover:underline" style={{ color: "#4a9eff" }}>
                Connect more platforms
              </a>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={post}
          disabled={sharing || selected.size === 0}
          data-testid={`${testidPrefix}-post`}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-black transition disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: "#4a9eff", fontSize: 12, fontWeight: 600 }}
        >
          <Share2 size={13} />
          {sharing ? "Queuing…" : `Post to ${selected.size} ${selected.size === 1 ? "platform" : "platforms"}`}
        </button>
      </div>
    </div>
  );
}

export default KaraokeSharePanel;
